// ============================================
// AlpenScreen - Super Admin Panel (Consolidated)
// File: frontend/src/components/SuperAdminPanel.js
// Purpose: All SuperAdmin features in one place with tabs
// ============================================

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Crown,
  Building2,
  Users,
  CreditCard,
  BarChart3,
  LayoutDashboard,
  Monitor,
  Shield,
  Activity 
} from 'lucide-react';
import SuperAdminOrganizations from './SuperAdminOrganizations';
import SuperAdminSubscriptions from './SuperAdminSubscriptions';
import SuperAdminUsers from './SuperAdminUsers';
import SuperAdminAnalytics from './SuperAdminAnalytics';
import SuperAdminAuditLog from '../pages/SuperAdminAuditLog';
import SuperAdminSystemHealth from '../pages/SuperAdminSystemHealth';

const SuperAdminPanel = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Übersicht aller Organisationen'
    },
    {
      id: 'users',
      label: 'Benutzer',
      icon: Users,
      description: 'Benutzerverwaltung über alle Organisationen'
    },
    {
      id: 'subscriptions',
      label: 'Subscriptions',
      icon: CreditCard,
      description: 'Abonnements & Billing'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      description: 'Globale Analytics'
    },
     {
    id: 'audit-log',
    label: 'Audit Log',
    icon: Shield,
    description: 'Activity Logs für Compliance'
  },
  {
    id: 'system-health',
    label: 'System Health',
    icon: Activity,
    description: 'Server & Performance Monitoring'
  }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <SuperAdminOrganizations />;
      case 'users':
        return <SuperAdminUsers />;
      case 'subscriptions':
        return <SuperAdminSubscriptions />;
      case 'analytics':
        return <SuperAdminAnalytics />;
         case 'audit-log':
      return <SuperAdminAuditLog />;
    case 'system-health':
      return <SuperAdminSystemHealth />;
      default:
        return <SuperAdminOrganizations />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 dark:from-purple-700 dark:to-purple-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                Super Admin Panel
              </h1>
              <p className="text-purple-100 text-sm mt-1">
                Zentrale Verwaltung aller Organisationen und Benutzer
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-1 overflow-x-auto" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group relative min-w-0 flex-1 overflow-hidden py-4 px-6 text-center font-medium transition-all
                    focus:z-10 focus:outline-none
                    ${isActive
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Icon 
                      size={20} 
                      className={isActive ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'}
                    />
                    <span className="hidden sm:inline text-sm font-semibold">
                      {tab.label}
                    </span>
                  </div>
                  
                  {/* Active indicator */}
                  {isActive && (
                    <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-purple-600 to-purple-500" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default SuperAdminPanel;